import React, { useState, useEffect, Fragment } from "react";
import Rating from "@material-ui/lab/Rating";
import useStyles from "./style";
import { CheckBox, InputField, RadioList } from "../../atoms/Forms";
import get from 'lodash/get';
import Ranking from "./subcomponents/Ranking";
import { Box } from "@material-ui/core";
import Typography from "../../atoms/Typography";
import MatrixChoiceGroup from "./subcomponents/MatrixChoiceGroup";
import Button from '../../atoms/Button'
import fetchService from '../../../../services/FetchService';

const DynamicForm = ({ apiEndpoint, originalProps }) => {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});

  const classes = useStyles();

  // fetch form questions from api
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const requestData = {
          url: `${get(originalProps, 'appConfig.MAIL_CHIMP_FORM_GET_QUESTIONS_API', '')}/${apiEndpoint}`,
          method: 'Get',
        };
        const response = await fetchService(requestData);
        const data = get(response, 'data', []) || []
        const activeQuestions = data.filter((q) => q.state === 0);
        setQuestions(activeQuestions);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };
    fetchQuestions();
  }, [apiEndpoint]);

  // matrix options change handling
  const handleChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleMultiChoiceChange = (questionId, option) => {
    setResponses((prev) => {
      const selected = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: selected.includes(option)
          ? selected.filter((o) => o !== option)
          : [...selected, option],
      };
    });
  };

  const handleDefaultValues = (questionId) => {
    const question = questions.find((question) => question?.questionId == questionId);
    if (!question) return null;
    const questionType = get(question, 'questionType', '')
    switch (questionType) {
      case "Ranking":
        return get(question, 'options', []) || [];
      case "Rating":
        return 0;
      case "MultiChoice":
        return [];
      case "MatrixChoice":
        return "";
      case "Choice":
        return "";
      case "Text":
        return "";
      default:
        return ""
    }
  }

  const handleResetForm = () => {
    const finalResponse = {};
    Object.keys(responses).forEach((key) => {
      finalResponse[key] = handleDefaultValues(key);
    });
    setResponses(finalResponse);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      SubmitDate: new Date().toISOString(),
      surveyId: get(questions, '[0].surveyId', ''),
      SurveyIdentifier: questions.length > 0 ? questions[0].sourceSurveyIdentifier : '',
      QuestionResponses: questions.map((q) => ({
        QuestionId: q.sourceQuestinIdentifier,
        Response: responses[q.questionId] ?? '',
      })),
    };

    try {
      await fetchService({
        url: `${get(originalProps, 'appConfig.MAIL_CHIMP_FORM_SUBMIT_API', '')}`,
        method: 'POST',
        data: payload
      })
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      handleResetForm();
    }
  };

  const RenderChoiceComponents = ({ question = null, fieldProps = {} }) => {
    const optionType = get(question, 'optionType', '');
    switch (optionType) {
      case "SingleChoice":
        fieldProps = {
          defaultValue: null,
          fieldName: { value: question.questionText },
          fieldLabel: { value: question.questionText },
          isRequired: { value: question.isResponseRequired },
          selectedValue: { value: responses[question.questionId] },
          listItems: question.options.map((option, i) => ({
            fields: {
              displayText: { value: option },
              key: { value: option },
              mappingId: { value: '' },
              id: `childId-${i}`
            },
            id: `parentId-${i}`
          }))
        }

        return <RadioList
          data={fieldProps}
          onChange={(e) => handleChange(question.questionId, e.target.value)}
          className={'fullWidth boldText choiceWrapper'}
        />;

      case "MultiChoice":
        fieldProps = (option) => ({
          fieldName: { value: question.questionText },
          fieldLabel: { value: option },
          fieldType: { value: 'Checkbox' },
          isRequired: { value: question.isResponseRequired },
        })

        return <Fragment>
          <Typography className={classes.questionLabel}>{question.questionText}</Typography>
          <Box className="CheckboxWrapper" style={{ display: 'flex', flexDirection: 'column', }}>
            {question.options.map((option, key) => (
              <Box key={key}>
                <CheckBox
                  data={fieldProps(option)}
                  selected={(responses[question.questionId] || []).includes(option)}
                  onChange={() => handleMultiChoiceChange(question.questionId, option)}
                  className={'fullWidth choiceWrapper'}
                />
              </Box>
            ))}
          </Box>
        </Fragment>

      default:
        return <></>
    }
  }

  return (
    <>
      {Boolean(questions.length) &&
        <>
          <Typography variant={'h5'} className={classes.heading}>
            {apiEndpoint}
          </Typography>
          <form onSubmit={handleSubmit} className={classes.root}>
            {questions.map((question, i) => {
              let fieldProps;
              if (question.sourceParentQuestinIdentifier) return null;
              if (question.questionType === 'Text') {
                fieldProps = {
                  fieldName: { value: question.questionText },
                  fieldLabel: { value: question.questionText },
                  isRequired: { value: question.isResponseRequired },
                  selectedValue: { value: responses[question.questionId] },
                  type: "text",
                }
              }
              return (
                <div key={question.questionId} className="surveyQuestion">
                  {question.questionType === 'MatrixChoiceGroup' && (
                    <MatrixChoiceGroup
                      parentQuestion={question}
                      questions={questions}
                      responses={responses}
                      handleChange={handleChange}
                    />
                  )}

                  {question.questionType === 'Text' && (
                    <InputField
                      className="inputFieldQuestion"
                      data={fieldProps}
                      onChange={(e) => handleChange(question.questionId, e.target.value)}
                    />
                  )}

                  {question.questionType === "Rating" && (
                    <>
                      <Typography className={classes.questionLabel}>{question.questionText}</Typography>
                      <Rating
                        className={classes.rating}
                        name={`rating-${question.questionId}`}
                        value={Number(responses[question.questionId]) || 0} // Ensure value is a number
                        onChange={(_, newValue) => handleChange(question.questionId, newValue)}
                        precision={1} // Allows whole star selection
                      />
                    </>
                  )}

                  {question.questionType === 'Choice' && (
                    <RenderChoiceComponents fieldProps={fieldProps} question={question} />
                  )}

                  {question.questionType === 'Ranking' && (
                    <Ranking
                      question={question}
                      response={responses[question.questionId]}
                      onChange={handleChange}
                    />
                  )}
                </div>
              );
            })}
            <Button
              buttonType={'form'}
              type={'submit'}
              hasBorder={false}
              size={'small'}
              field={{ value: { text: 'Submit' } }}
              className={`${classes.submitButton}`}
              disabled={false}
            />
          </form>
        </>
      }
    </>

  );
};

export default DynamicForm;
